import {
  AUDIENCE_MODE,
  CANCELLABLE_STATUSES,
  ORDER_ORIGIN,
  ORDER_STATUS,
  WIZARD_STEP,
} from './constants';

/**
 * Maps an existing order status to the wizard step the member should resume at.
 * Returns null when the order can no longer be edited in the wizard.
 */
export const getWizardStepForOrder = (order) => {
  switch (order?.status) {
    case ORDER_STATUS.DRAFT:
      return WIZARD_STEP.AUDIENCE;
    case ORDER_STATUS.RESOLVING:
    case ORDER_STATUS.RESOLUTION_FAILED:
      return WIZARD_STEP.RESOLUTION;
    case ORDER_STATUS.REVIEW:
      return WIZARD_STEP.REVIEW;
    // An interrupted pay resumes on the Pay step in a processing state; the
    // wizard polls until the debit settles (→ PAID) or unlocks (→ REVIEW).
    case ORDER_STATUS.PAYING:
      return WIZARD_STEP.PAY;
    default:
      return null;
  }
};

/**
 * True for a print ticket created from a book order (origin 'book'). Those are
 * not Mail on Demand mailings: they are managed in ABO, have no recipients or
 * MOD pricing, and a member cancel would not refund the book charge. DS keeps
 * them out of the member endpoints; this is the client-side safety net in case
 * one still reaches the UI (afy-ui deployed before DS, or a cached response).
 */
export const isBookPrintOrder = (order) => order?.origin === ORDER_ORIGIN.BOOK;

/** Whether the member may cancel this order from the order detail page. */
export const isMemberCancellable = (order) =>
  Boolean(order) && CANCELLABLE_STATUSES.includes(order.status) && !isBookPrintOrder(order);

/** Drops book print tickets from the member's "My mail orders" list. */
export const excludeBookPrintOrders = (orders) =>
  Array.isArray(orders) ? orders.filter((order) => !isBookPrintOrder(order)) : orders;

export const parseTagsInput = (value = '') =>
  value
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

export const buildAudienceCriteria = (mode, tagsInput) => {
  if (mode === AUDIENCE_MODE.TAGS) {
    return { mode, tags: parseTagsInput(tagsInput) };
  }

  return { mode: AUDIENCE_MODE.ALL };
};

export const isSameAudienceCriteria = (a, b) => {
  if (!a || !b) return false;
  if (a.mode !== b.mode) return false;
  if (a.mode === AUDIENCE_MODE.ALL) return true;

  const tagsA = [...(a.tags || [])].sort();
  const tagsB = [...(b.tags || [])].sort();

  return tagsA.length === tagsB.length && tagsA.every((tag, index) => tag === tagsB[index]);
};

// A draft may only be reused when it references the SAME asset; otherwise a
// member who went Back and picked a different asset would silently pay for
// the original one.
export const isSameAsset = (a, b) => {
  if (!a || !b) return false;

  return a.kind === b.kind && a.sourceId === b.sourceId;
};

export const formatDate = (value) => {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

export const formatCredits = (value) => {
  const numeric = Number(value || 0);

  return numeric.toLocaleString('en-US');
};
